import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import NotificationService from "../../Services/Account/NotificationService";
import signalRService from "../../Services/SignalR/SignalRService";
import { getToken } from "../../Utils/Auth";
import { PortalTypeEnum } from "../../Utils/Common/Enums/Enums";

/**
 * Post login bootstrap for notifications and SignalR.
 *
 * 1. Loads the user's notifications, which is also how the client receives the
 *    per-endpoint Azure Function keys (`data.functionKeys`).
 * 2. Only once that has settled does the SignalR connection start, because
 *    negotiate needs its `x-functions-key`.
 *
 * Notifications load for every portal type; the connection itself stays
 * limited to the Admin portal.
 */
export const useSignalR = () => {
  const token = getToken();
  const isInitializedRef = useRef(false);
  const initializationPromiseRef = useRef<Promise<void> | null>(null);
  const notificationsRequestedRef = useRef(false);
  const [functionKeysReady, setFunctionKeysReady] = useState(false);

  // Get portal type from Redux store
  const portalType = useSelector(
    (state: any) =>
      state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.adminType ||
      state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.portalType
  );

  // Step 1: right after login, fetch notifications and the function keys
  useEffect(() => {
    if (!token || notificationsRequestedRef.current) {
      return;
    }

    notificationsRequestedRef.current = true;
    NotificationService.loadUserNotifications()
      .catch((error) => {
        console.error("Failed to load notifications after login:", error);
        notificationsRequestedRef.current = false;
      })
      // Connect either way: getFunctionHeaders falls back to the env var and
      // an anonymous function app needs no key at all.
      .finally(() => setFunctionKeysReady(true));
  }, [token]);

  // Step 2: connect once the keys have been fetched
  useEffect(() => {
    if (
      token &&
      functionKeysReady &&
      !isInitializedRef.current &&
      portalType === PortalTypeEnum.Admin
    ) {
      // Prevent multiple simultaneous initializations
      if (!initializationPromiseRef.current) {
        isInitializedRef.current = true;
        initializationPromiseRef.current = signalRService
          .initializeConnection()
          .then(() => {
            console.log("SignalR connections initialized");
          })
          .catch((error) => {
            console.error("Failed to initialize SignalR:", error);
            isInitializedRef.current = false; // Allow retry on error
          })
          .finally(() => {
            initializationPromiseRef.current = null;
          });
      }
    }

    // Cleanup on unmount, when token is removed, or when portalType changes away from Admin
    return () => {
      if (
        (!token || portalType !== PortalTypeEnum.Admin) &&
        isInitializedRef.current
      ) {
        signalRService.disconnect().then(() => {
          isInitializedRef.current = false;
          initializationPromiseRef.current = null;
        });
      }
    };
  }, [token, portalType, functionKeysReady]);

  return {
    isConnected: signalRService.isConnected(),
  };
};

export default useSignalR;
